'use client';
import React, { useState, useEffect, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import Icon from '@/components/ui/AppIcon';
import Breadcrumb from '@/components/Breadcrumb';

const supabase = createClient();

interface Referral {
  id: string;
  codice: string;
  stato: string;
  reward_tipo: string;
  reward_valore: number;
  reward_applicato: boolean;
  created_at: string;
}

interface Struttura {
  id: string;
  nome: string;
  slug: string;
}

export default function ReferralContent() {
  const [referrals, setReferrals] = useState<Referral[]>([]);
  const [struttura, setStruttura] = useState<Struttura | null>(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState(false);
  const { profile } = useAuth();

  const fetchData = useCallback(async () => {
    setLoading(true);
    const [{ data: r }, { data: s }] = await Promise.all([
      supabase.from('referral_programma').select('*').order('created_at', { ascending: false }),
      supabase.from('strutture').select('id, nome, slug').limit(1).single(),
    ]);
    setReferrals(r || []);
    setStruttura(s || null);
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const generateCodice = async () => {
    if (!struttura) return;
    setGenerating(true);
    await supabase.from('referral_programma').insert({
      struttura_id: struttura.id,
      reward_tipo: 'sconto',
      reward_valore: 20,
    });
    setGenerating(false);
    fetchData();
  };

  const copyLink = (codice: string) => {
    const url = `${window.location.origin}/registrati?ref=${codice}`;
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const attivi = referrals.filter(r => r.stato === 'attivo');
  const usati = referrals.filter(r => r.stato === 'usato');
  const rewardTotale = usati.reduce((sum, r) => sum + (r.reward_valore || 0), 0);

  return (
    <div className="space-y-6">
      <Breadcrumb items={[{ label: 'Programma Referral' }]} />
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Programma Referral</h1>
          <p className="text-text-secondary text-sm mt-1">Invita altre strutture e guadagna sconti sul tuo abbonamento</p>
        </div>
        <button onClick={generateCodice} disabled={generating}
          className="flex items-center gap-2 bg-sky-600 hover:bg-sky-500 disabled:opacity-50 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors">
          <Icon name="PlusIcon" size={16} /> Genera Codice
        </button>
      </div>

      {/* How it works */}
      <div className="bg-gradient-to-r from-sky-500/10 to-purple-500/10 border border-sky-500/20 rounded-xl p-6">
        <h2 className="font-semibold text-white mb-4">Come funziona</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { step: '1', title: 'Genera il tuo codice', desc: 'Crea un codice referral univoco per la tua struttura', icon: 'KeyIcon' },
            { step: '2', title: 'Condividi con colleghi', desc: 'Invia il link a strutture sanitarie che potrebbero beneficiare di GestioneLab', icon: 'ShareIcon' },
            { step: '3', title: 'Guadagna sconti', desc: 'Per ogni struttura che si registra con il tuo codice, ottieni il 20% di sconto', icon: 'GiftIcon' },
          ].map(s => (
            <div key={s.step} className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-sky-500/20 flex items-center justify-center flex-shrink-0">
                <span className="text-sky-400 font-bold text-sm">{s.step}</span>
              </div>
              <div>
                <p className="font-medium text-white text-sm">{s.title}</p>
                <p className="text-xs text-muted-color mt-0.5">{s.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: 'Codici Attivi', value: attivi.length, color: 'text-green-400', icon: 'CheckCircleIcon' },
          { label: 'Referral Completati', value: usati.length, color: 'text-sky-400', icon: 'UserPlusIcon' },
          { label: 'Reward Totale', value: `${rewardTotale}%`, color: 'text-amber-400', icon: 'GiftIcon' },
        ].map(s => (
          <div key={s.label} className="bg-surface-elevated border border-border rounded-xl p-4">
            <div className="flex items-center gap-2 mb-1">
              <Icon name={s.icon as any} size={16} className={s.color} />
              <span className="text-xs text-muted-color">{s.label}</span>
            </div>
            <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Referral codes */}
      {loading ? (
        <div className="flex items-center justify-center py-8"><div className="w-6 h-6 border-2 border-sky-500 border-t-transparent rounded-full animate-spin" /></div>
      ) : referrals.length === 0 ? (
        <div className="text-center py-12 bg-surface-elevated border border-border rounded-xl text-muted-color">
          <Icon name="GiftIcon" size={40} className="mx-auto mb-3 opacity-30" />
          <p>Nessun codice referral. Genera il primo!</p>
        </div>
      ) : (
        <div className="space-y-3">
          <h2 className="text-sm font-semibold text-muted-color uppercase tracking-wider">I tuoi Codici Referral</h2>
          {referrals.map(ref => (
            <div key={ref.id} className="bg-surface-elevated border border-border rounded-xl p-4 flex items-center gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <code className="text-lg font-bold text-sky-400 tracking-widest">{ref.codice}</code>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${ref.stato === 'attivo' ? 'bg-green-500/10 text-green-400' : ref.stato === 'usato' ? 'bg-sky-500/10 text-sky-400' : 'bg-gray-500/10 text-gray-400'}`}>
                    {ref.stato}
                  </span>
                </div>
                <p className="text-xs text-muted-color">
                  Reward: {ref.reward_valore}% {ref.reward_tipo} · {new Date(ref.created_at).toLocaleDateString('it-IT')}
                  {ref.reward_applicato && <span className="ml-2 text-green-400">✓ Applicato</span>}
                </p>
              </div>
              {ref.stato === 'attivo' && (
                <button onClick={() => copyLink(ref.codice)}
                  className={`flex items-center gap-2 text-sm px-3 py-1.5 rounded-lg transition-colors ${copied ? 'bg-green-500/10 text-green-400' : 'bg-surface hover:bg-surface-elevated border border-border text-white'}`}>
                  <Icon name={copied ? 'CheckIcon' : 'ClipboardDocumentIcon'} size={14} />
                  {copied ? 'Copiato!' : 'Copia Link'}
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
